/**
 * Configured vs Effective vs Health — operator paint + refuse reasons.
 * Soft≠ invent AUTO. AUTO + missing Run is a system fault, not leftover-approve.
 */

/** Match api/ai_autonomy/live_gap.py GAP_REASONS — never invent GPS/remux proof. */
export const LIVE_GAP_REASONS = Object.freeze([
  "no_fresh_responder",
  "fresh_person_requires_human",
  "no_alarm_location",
  "origin_unbound",
  "camera_digest_required",
  "camera_id_required",
  "dispatch_resource_site_unknown",
  "live_quota_user",
  "live_quota_device",
  "live_quota_console",
  "live_quota_tenant",
  "live_quota_global",
  "live_quota_i1_starved",
  "live_quota_alarm_reserve",
]);

export function firstLiveGapReason(items) {
  const list = Array.isArray(items) ? items : [];
  for (const it of list) {
    const raw = String((it && it.reason) || "").trim();
    const tail = raw.includes(":") ? raw.split(":").pop().trim() : raw;
    if (LIVE_GAP_REASONS.indexOf(tail) >= 0) return tail;
  }
  return "";
}

export function autonomyPlanesOf(autonomy) {
  const a = autonomy && typeof autonomy === "object" ? autonomy : {};
  const cve =
    a.configured_vs_effective && typeof a.configured_vs_effective === "object"
      ? a.configured_vs_effective
      : a;
  const configured = a.configured === true;
  const configuredMode = String(cve.configured_mode || a.configured_mode || "");
  const effectiveMode = String(cve.effective_mode || a.effective_mode || "");
  const health = String(cve.runtime_health || a.runtime_health || "");
  const ledger = String(cve.ledger || a.ledger || "");
  const reason = String(a.reason || a.inspect_reason || "");
  const fault = a.run_missing_fault === true || a.unknown_run_fault === true;
  return {
    configured,
    configured_mode: configured ? configuredMode : "",
    effective_mode: effectiveMode,
    runtime_health: health,
    ledger,
    manual_only: a.manual_only === true || !configured,
    fault,
    reason,
    team_run_id: String(a.team_run_id || ""),
    team_roles: Array.isArray(a.team_roles)
      ? a.team_roles.map((r) => String(r || "").trim()).filter(Boolean).slice(0, 8)
      : [],
    team_n: Number(a.team_n || 0),
    team_scale_reason: String(a.team_scale_reason || ""),
    remux_session_id: String(a.remux_session_id || ""),
    remux_media_played: a.remux_media_played === true,
    skill_live: a.skill_live === true,
    skill_snapshot: a.skill_snapshot === true,
    skill_record: a.skill_record === true,
    skill_close: a.skill_close === true,
    live_n: Number(a.live_n || 0) || 0,
    playing_n: Number(a.playing_n || 0) || 0,
    closed_n: Number(a.closed_n || 0) || 0,
    live_loops_ok: a.live_loops_ok === true,
    close_ready: a.close_ready === true,
    close_wait: String(a.close_wait || ""),
    policy_rev: Number(a.policy_rev || cve.policy_rev || 0),
    families_ack: a.families_ack === true,
    why_blocked: String(a.why_blocked || reason || ""),
    decision_owner: String(a.decision_owner || ""),
    rules_role: String(a.rules_role || ""),
    execution_state: String(a.execution_state || ""),
    typed_intents_n: Number(a.typed_intents_n || 0),
    tombstone_active: a.tombstone_active === true,
    tombstone_reason: String(a.tombstone_reason || ""),
    flag_revision: Number(a.flag_revision || 0),
    flag_ack_n: Number(a.flag_ack_n || 0),
    flag_ack_expected: Number(a.flag_ack_expected || 0),
    flag_lag: a.flag_lag === true,
    flag_sot: String(a.flag_sot || ""),
    origin_source: String(a.origin_source || ""),
    inspect_unavailable: a.inspect_unavailable === true,
  };
}

export function fourVisiblesOf(planes, opts) {
  const p = planes && typeof planes === "object" ? planes : {};
  const loading = !!(opts && opts.loading) || !!p.inspect_unavailable;
  const exp = Number(p.flag_ack_expected || 0);
  const ackN = Number(p.flag_ack_n || 0);
  const hasFlag = Boolean(p.flag_sot) || exp > 0;
  let ack = "…";
  if (loading) {
    ack = "…";
  } else if (hasFlag && exp) {
    ack = String(ackN) + "/" + String(exp) + (p.flag_lag ? " lag" : "");
  } else if (p.families_ack) {
    ack = "ACK";
  } else if (hasFlag) {
    ack = "lag";
  }
  const rev = [];
  if (p.policy_rev) rev.push("p" + String(p.policy_rev));
  if (p.flag_revision) rev.push("f" + String(p.flag_revision));
  return {
    desired: loading
      ? "…"
      : (p.configured ? p.configured_mode || "—" : "off") + "/" + (p.effective_mode || "—"),
    rev: loading ? "…" : (rev.join("·") || "—"),
    ack,
    why: loading
      ? "…"
      : p.tombstone_active
        ? "tombstone:" + (p.tombstone_reason || "held")
        : p.why_blocked || (p.decision_owner === "none" ? "no plan" : "—"),
    origin: loading ? "…" : String(p.origin_source || ""),
  };
}

export function continueRefuseReasonFromInspect(eff) {
  if (!eff || typeof eff !== "object") return "needs_team_run";
  if (eff.run_missing_fault === true) return "configured_auto_run_missing";
  if (eff.unknown_run_fault === true) return "run_unknown_needs_reconcile";
  const r = String(eff.inspect_reason || eff.reason || "");
  if (r === "configured_auto_run_missing" || r === "run_unknown_needs_reconcile") return r;
  if (r === "policy_paused_no_run") return r;
  if (eff.manual_only === true || eff.configured === false) return "manual_only_never_configured";
  return "needs_team_run";
}

export function continueRefuseToast(reason) {
  const r = String(reason || "");
  if (r === "configured_auto_run_missing") {
    return "AI is AUTO but no Mission Run exists — system fault, not leftover approve";
  }
  if (r === "run_unknown_needs_reconcile") {
    return "Mission Run is UNKNOWN — reconcile, do not mint a second run";
  }
  if (r === "manual_only_never_configured") {
    return "AI is not configured — no Mission Run, no Continue";
  }
  if (r === "policy_paused_no_run") {
    return "Policy is PAUSED — no Mission Run until AUTO";
  }
  if (r === "needs_team_run") {
    return "Continue needs an open Mission Run — open Mission Room first";
  }
  return "Continue refused · Team Runtime";
}

export function holdRefuseToast(reason) {
  const r = String(reason || "");
  if (r === "configured_auto_run_missing") {
    return "AI is AUTO but no Mission Run exists — system fault, not leftover Hold";
  }
  if (r === "run_unknown_needs_reconcile") {
    return "Mission Run is UNKNOWN — reconcile, do not mint a second run";
  }
  if (r === "manual_only_never_configured") {
    return "AI is not configured — no Mission Run, no Hold";
  }
  if (r === "policy_paused_no_run") {
    return "Policy is PAUSED — no Mission Run to Hold";
  }
  if (r === "needs_team_run") {
    return "Hold needs an open Mission Run — open Mission Room first";
  }
  return "Hold refused · Team Runtime";
}
